import * as t from "io-ts";
import { TypeOf } from "io-ts";
import { DateFromISOString, withFallback } from "io-ts-types";

// Guest filter options
export const GuestFilterIO = t.union([
    t.literal("ALL"),
    t.literal("ACCEPTED"),
    t.literal("CHECKED_IN"), // Note: controller uses 'CHECKED_IN' but checks for !== 'NONE'
]);

// Document naming strategy
export const DocumentNamingIO = t.union([t.literal("guest_uuid"), t.literal("guest_field")]);

export const DeletionRuleIO = t.union([
    t.literal("NONE"),
    t.literal("1WEEK"),
    t.literal("2WEEK"),
    t.literal("1MONTH"),
    t.literal("3MONTHS"),
    t.literal("6MONTHS"),
]);

// Output settings from frontend
export const OutputSettingsIO = t.type({
    documentName: t.union([t.string, t.null]),
    guestFilter: withFallback(GuestFilterIO, "ALL"),
    documentNaming: withFallback(DocumentNamingIO, "guest_uuid"),
    customFieldDefinitionId: t.union([t.string, t.null]),
    // true = overwrite existing field value, false = skip guest (no document created)
    customFieldOverwrite: withFallback(t.boolean, false),
    deletionRule: DeletionRuleIO,
    deleteDateFieldDefinitionId: withFallback(t.union([t.string, t.null]), null),
    // Kept for backward compatibility with persisted batch metadata — no longer used in controller logic.
    // The deletion date field is always written when a deleteDateFieldDefinitionId is set.
    deleteDateFieldOverwrite: withFallback(t.boolean, false),
});

// Progress/Processing states
export const StateIO = t.keyof({
    READY: null,
    RUNNING: null,
    FINISHED: null,
    COMPLETED_WITH_ERRORS: null,
    FAILED: null,
});

// Document folder (batch folder) returned by getDocumentFolders endpoint
export const DocumentFolderIO = t.type({
    id: t.string, // Batch ID
    name: t.union([t.string, t.null]), // Document name from outputSettings
    filename: t.string, // Folder filename
    url: t.string, // Full URL to access folder
    createdAt: DateFromISOString, // Batch creation date
    updatedAt: t.union([DateFromISOString, t.null]), // Last modification date
    outputSettings: OutputSettingsIO,
    successCount: t.union([t.number, t.undefined]), // Number of successfully processed documents
    failureCount: t.union([t.number, t.undefined]), // Number of failed document processing attempts
    skippedCount: t.union([t.number, t.undefined]), // Number of guests skipped (field already filled, overwrite disabled)
    totalCount: t.union([t.number, t.undefined]), // Total number of documents to process
    processingStatus: t.union([StateIO, t.undefined]), // Processing status using ProgressState enum
});

// Generated PDF file returned by getDocumentFiles endpoint
export const DocumentFileIO = t.type({
    id: t.string, // MD5 hash of the file name (used as unique identifier)
    name: t.string, // Original file name
    filename: t.string, // File filename property (PDF file)
    url: t.string, // Full URL to access/download the PDF file
    updatedAt: t.union([DateFromISOString, t.null]), // Timestamp of last modification
});

export const DocumentFolderListIO = t.array(DocumentFolderIO);
export const DocumentFileListIO = t.array(DocumentFileIO);

export const ProgressIO = t.type({
    id: t.string,
    progress: t.number,
    size: t.union([t.number, t.undefined]),
    state: StateIO,
    updatedAt: DateFromISOString, // Automatically parses ISO string to Date object
});

export const DocumentsCreateResponseIO = t.type({
    batchId: t.string, // Updated from progressId to match backend
    count: t.number,
    message: t.string,
    eventId: t.string,
    folderUrl: t.string,
    // Note: removed filename and fileUrl as they're not in the new backend response
});

export const GroupedErrorIO = t.type({
    error: t.string,
    count: t.number,
    errorCode: t.number,
    guestIds: t.array(t.string),
});

export const GroupedErrorListIO = t.array(GroupedErrorIO);

export type DocumentsCreateResponse = t.TypeOf<typeof DocumentsCreateResponseIO>;

export type DocumentFile = TypeOf<typeof DocumentFileIO>;
export type DocumentFolder = TypeOf<typeof DocumentFolderIO>;
export type DeletionRule = TypeOf<typeof DeletionRuleIO>;
export type GroupedError = TypeOf<typeof GroupedErrorIO>;

export type State = TypeOf<typeof StateIO>;
export type Progress = TypeOf<typeof ProgressIO>;
